import React, { useMemo } from "react";
import { API } from "@/lib/api";
import { Trophy, CheckCircle, FileText, Package, Boat } from "@phosphor-icons/react";
import { formatCurrency } from "@/lib/currency";
import { Meta } from "./shared";

export const WinnerView = ({ a, isCreator }) => {
  // BOP RFQ closed with per-line awards: render the dedicated awards view
  if (a.type === "bop" && (a.line_awards || a.my_line_wins || a.my_line_wins_total != null)) {
    return <BopAwardsView a={a} isCreator={isCreator} />;
  }
  // Freight auction closed with per-shipment allocations
  if (a.type === "freight" && (a.allocations || a.my_shipment_wins || a.my_shipment_wins_total != null)) {
    return <FreightAllocationsView a={a} isCreator={isCreator} />;
  }
  const w = a.winner;
  const noWinner = !w;
  const closedWithoutWinner = a.closed_without_winner;
  // For losing bidders, the backend redacts the winner block entirely but sets
  // closed_with_winner=true so we know the auction was awarded to someone else.
  const losingBidderClosed = !isCreator && noWinner && a.closed_with_winner === true;
  let headingLabel;
  if (losingBidderClosed) headingLabel = "Auction Closed";
  else if (noWinner) headingLabel = closedWithoutWinner ? "Closed Without Winner" : "Auction Closed";
  else headingLabel = "Winner Declared";

  let emptyMessage = "No winner — no bids were placed.";
  if (losingBidderClosed) {
    emptyMessage = "This auction has been closed and a winner was declared. Your bid was not selected this time.";
  } else if (closedWithoutWinner) {
    emptyMessage = "The creator closed this auction without awarding it. No bid was selected as the winner.";
  }

  return (
    <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="winner-view">
      <div className="absolute top-0 right-0 bg-[#0B1929] text-white px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Closed</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
        <Trophy size={14} weight="bold" /> {headingLabel}
      </div>
      {noWinner ? (
        <div data-testid="no-winner-block">
          <div className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest leading-none">{emptyMessage}</div>
          {closedWithoutWinner && (
            <p className="text-sm text-neutral-600 mt-4 max-w-xl">
              Bids that were submitted will not be honored. The creator can create a new auction if they wish to re-procure this requirement.
            </p>
          )}
          {losingBidderClosed && (
            <p className="text-sm text-neutral-600 mt-4 max-w-xl">
              Thank you for participating. The winning price and winner identity are confidential. We appreciate the time and effort you put into your quote and hope to see you on a future auction.
            </p>
          )}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="font-display font-extrabold tracking-tightest text-6xl lg:text-7xl leading-none klein-blue" data-testid="winning-price">
              {formatCurrency(w.price, a.currency)}
            </div>
            <div className="mt-3 text-sm text-neutral-600">Winning Bid · Round {w.round}</div>
            {isCreator && (
              <div className="mt-6 space-y-2" data-testid="winner-identity">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Awarded To</div>
                <div className="font-display font-extrabold text-2xl tracking-tightest">{w.bidder_name}</div>
                <div className="text-sm font-mono-plex">{w.bidder_email}</div>
                {w.bidder_company && <div className="text-sm text-neutral-600">{w.bidder_company}</div>}
              </div>
            )}
            {!isCreator && a.you_won && (
              <div className="mt-6 inline-flex items-center gap-2 text-green-700" data-testid="you-won-banner">
                <CheckCircle size={16} weight="bold" />
                <span className="text-sm font-semibold">You won this auction.</span>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <Meta label="Delivery" value={w.delivery_timeline} />
            <Meta label="Payment Terms" value={w.payment_terms} />
            <Meta label="Warranty" value={w.warranty} />
            {w.remarks && <Meta label="Remarks" value={w.remarks} />}
            {w.attachment_path && (
              <a
                href={`${API}/files/download?path=${encodeURIComponent(w.attachment_path)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] font-semibold underline underline-offset-4"
                data-testid="winner-attachment"
              >
                <FileText size={14} weight="bold" /> {w.attachment_filename || "Attachment"}
              </a>
            )}
          </div>
        </div>
      )}
      {isCreator && a.all_bids && a.all_bids.length > 0 && (
        <AllBidsTable bids={a.all_bids} currency={a.currency} />
      )}
    </div>
  );
};

const AllBidsTable = ({ bids, currency }) => {
  const remarksBids = useMemo(() => bids.filter((b) => b.remarks), [bids]);
  return (
  <div className="mt-10 border-t-2 border-black pt-8" data-testid="all-bids-section">
    <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
      <h3 className="font-display font-extrabold text-2xl tracking-tightest">All Bids — Now Revealed</h3>
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{bids.length} {bids.length === 1 ? "bid" : "bids"}</span>
    </div>
    <p className="text-xs text-neutral-500 mt-3 max-w-2xl">
      Identities of all bidders are revealed below now that the auction has closed and a winner was declared. Bids are sorted by round, then by lowest price. Ties on the lowest price were broken by who submitted first.
    </p>
    <div className="overflow-x-auto mt-4">
      <table className="w-full border-collapse text-sm" data-testid="all-bids-table">
        <thead>
          <tr className="border-b border-black/30 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
            <th className="text-left py-2 pr-3">Round</th>
            <th className="text-left py-2 pr-3">Bidder</th>
            <th className="text-left py-2 pr-3">Company</th>
            <th className="text-right py-2 pr-3">Price</th>
            <th className="text-left py-2 pr-3">Delivery</th>
            <th className="text-left py-2 pr-3">Payment</th>
            <th className="text-left py-2 pr-3">Warranty</th>
            <th className="text-left py-2 pr-3">Attachment</th>
          </tr>
        </thead>
        <tbody>
          {bids.map((b, idx) => (
            <tr
              key={`${b.bidder_id}-${b.round}-${idx}`}
              className={`border-b border-black/10 ${b.is_winner ? "bg-[#F1F5FF]" : ""}`}
              data-testid={`all-bid-row-${idx}`}
            >
              <td className="py-3 pr-3 font-mono-plex">R{b.round}</td>
              <td className="py-3 pr-3">
                <div className="font-semibold">{b.bidder_name}</div>
                <div className="text-[11px] font-mono-plex text-neutral-500">{b.bidder_email}</div>
                {b.is_winner && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 bg-[#22D3EE] text-[#0B1929] font-semibold text-[9px] tracking-[0.2em] uppercase font-semibold">
                    Winner
                  </span>
                )}
              </td>
              <td className="py-3 pr-3 text-neutral-600">{b.bidder_company || "—"}</td>
              <td className={`py-3 pr-3 text-right font-mono-plex ${b.is_winner ? "klein-blue font-bold" : ""}`}>
                {formatCurrency(b.price, currency)}
              </td>
              <td className="py-3 pr-3 text-neutral-700">{b.delivery_timeline || "—"}</td>
              <td className="py-3 pr-3 text-neutral-700">{b.payment_terms || "—"}</td>
              <td className="py-3 pr-3 text-neutral-700">{b.warranty || "—"}</td>
              <td className="py-3 pr-3">
                {b.attachment_path ? (
                  <a
                    href={`${API}/files/download?path=${encodeURIComponent(b.attachment_path)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs uppercase tracking-[0.2em] font-semibold underline underline-offset-4"
                  >
                    <FileText size={12} weight="bold" className="inline mr-1" />
                    {b.attachment_filename || "View"}
                  </a>
                ) : (
                  <span className="text-neutral-400">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {remarksBids.length > 0 && (
      <div className="mt-6 space-y-3">
        <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">Remarks</div>
        {remarksBids.map((b, idx) => (
          <div key={`r-${idx}`} className="text-sm border-l-2 border-black/20 pl-3 py-1">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-0.5">
              R{b.round} · {b.bidder_name} {b.is_winner && "· (winner)"}
            </div>
            <div className="text-neutral-700">{b.remarks}</div>
          </div>
        ))}
      </div>
    )}
  </div>
  );
};


const BopAwardsView = ({ a, isCreator }) => {
  const awards = a.line_awards || [];
  const myWins = a.my_line_wins || [];
  const totalAwardedValue = useMemo(
    () => Math.round((awards.reduce((s, x) => s + (x.line_total || 0), 0)) * 100) / 100,
    [awards],
  );
  const winnersByBidder = useMemo(() => {
    const m = {};
    for (const aw of awards) {
      const k = aw.bidder_id;
      if (!k) continue;
      if (!m[k]) m[k] = { name: aw.bidder_name, email: aw.bidder_email, company: aw.bidder_company, items: [], total: 0 };
      m[k].items.push(aw);
      m[k].total += aw.line_total || 0;
    }
    return Object.values(m);
  }, [awards]);

  // ----- Bidder view (private to them: their own wins only) -----
  if (!isCreator) {
    if (myWins.length === 0) {
      return (
        <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="bop-bidder-no-win">
          <div className="absolute top-0 right-0 bg-[#0B1929] text-white px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Closed</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
            <Trophy size={14} weight="bold" /> Auction Closed
          </div>
          <div className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest leading-none">
            Your quote wasn&apos;t selected on any line this time.
          </div>
          <p className="text-sm text-neutral-600 mt-4 max-w-xl">
            Thank you for participating. Items on this RFQ were awarded to other vendors. Wins on other vendors&apos; lines are confidential — we don&apos;t reveal who got what. We hope to see you on the next BOP RFQ.
          </p>
        </div>
      );
    }
    return (
      <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="bop-bidder-won">
        <div className="absolute top-0 right-0 bg-[#22D3EE] text-[#0B1929] font-semibold px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Awarded</div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
          <Trophy size={14} weight="bold" /> You won {myWins.length} item{myWins.length === 1 ? "" : "s"}
        </div>
        <div className="grid lg:grid-cols-2 gap-10 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Your awarded value</div>
            <div className="font-display font-extrabold tracking-tightest text-5xl lg:text-6xl leading-none klein-blue" data-testid="my-won-total">
              {formatCurrency(a.my_line_wins_total || 0, a.currency)}
            </div>
          </div>
          <div className="text-sm text-neutral-600 leading-relaxed">
            Congratulations! The creator has awarded these items to you. They will contact you for next steps. Items not listed below were awarded to other vendors and their details remain confidential.
          </div>
        </div>
        <BopAwardsTable rows={myWins} currency={a.currency} hideBidder testid="my-wins-table" />
      </div>
    );
  }

  // ----- Creator (and super-admin) view -----
  return (
    <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="bop-creator-awarded">
      <div className="absolute top-0 right-0 bg-[#0B1929] text-white px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Closed</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
        <Package size={14} weight="bold" /> BOP RFQ Awarded · {winnersByBidder.length} vendor(s)
      </div>
      <div className="grid lg:grid-cols-2 gap-10 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Total RFQ value</div>
          <div className="font-display font-extrabold tracking-tightest text-5xl lg:text-6xl leading-none klein-blue" data-testid="bop-total-rfq-value">
            {formatCurrency(totalAwardedValue, a.currency)}
          </div>
          <div className="text-xs text-neutral-600 mt-2">{awards.length} item(s) awarded across {winnersByBidder.length} vendor(s)</div>
        </div>
        <div className="space-y-3" data-testid="bop-vendor-breakdown">
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">Vendor Breakdown</div>
          {winnersByBidder.map((v) => (
            <div key={v.email} className="border border-black/10 p-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-semibold">{v.name || v.email}</div>
                <div className="font-mono-plex font-semibold">{formatCurrency(v.total, a.currency)}</div>
              </div>
              <div className="text-[11px] font-mono-plex text-neutral-500">{v.email}{v.company ? ` · ${v.company}` : ""}</div>
              <div className="text-xs text-neutral-600 mt-1">{v.items.length} item{v.items.length === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      </div>
      <BopAwardsTable rows={awards} currency={a.currency} testid="bop-awards-table" />
    </div>
  );
};

const BopAwardsTable = ({ rows, currency, hideBidder, testid }) => (
  <div className="overflow-x-auto border-t border-black/10 pt-4" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-black/30 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
          <th className="text-left p-2 w-10">#</th>
          <th className="text-left p-2">Item</th>
          {!hideBidder && <th className="text-left p-2">Awarded To</th>}
          <th className="text-right p-2 w-24">Qty</th>
          <th className="text-right p-2 w-32">Unit Price</th>
          <th className="text-right p-2 w-32">Line Total</th>
          <th className="text-left p-2 w-16">UOM</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.item_number}-${i}`} className="border-b border-black/5">
            <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
            <td className="p-2">
              <div className="font-mono-plex font-semibold">{r.item_number}</div>
              <div className="text-xs text-neutral-600">{r.item_desc}</div>
            </td>
            {!hideBidder && (
              <td className="p-2">
                <div className="font-semibold">{r.bidder_name || r.bidder_email}</div>
                <div className="text-[11px] font-mono-plex text-neutral-500">{r.bidder_email}</div>
              </td>
            )}
            <td className="p-2 text-right font-mono-plex">{Number(r.forecast_qty || 0).toLocaleString()}</td>
            <td className="p-2 text-right font-mono-plex">{formatCurrency(r.unit_price, currency)}</td>
            <td className="p-2 text-right font-mono-plex klein-blue font-semibold">{formatCurrency(r.line_total, currency)}</td>
            <td className="p-2 text-xs">{r.uom || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);


const FreightAllocationsView = ({ a, isCreator }) => {
  const allocations = a.allocations || [];
  const myWins = a.my_shipment_wins || [];
  const totalAwardedValue = useMemo(
    () => Math.round((allocations.reduce((s, x) => s + (x.rate || 0), 0)) * 100) / 100,
    [allocations],
  );
  const winnersByBidder = useMemo(() => {
    const m = {};
    for (const aw of allocations) {
      const k = aw.bidder_id;
      if (!k) continue;
      if (!m[k]) m[k] = { name: aw.bidder_name, email: aw.bidder_email, company: aw.bidder_company, items: [], total: 0 };
      m[k].items.push(aw);
      m[k].total += aw.rate || 0;
    }
    return Object.values(m);
  }, [allocations]);

  // ----- Bidder view (private: their own containers only) -----
  if (!isCreator) {
    if (myWins.length === 0) {
      return (
        <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="freight-bidder-no-win">
          <div className="absolute top-0 right-0 bg-[#0B1929] text-white px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Closed</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
            <Trophy size={14} weight="bold" /> Auction Closed
          </div>
          <div className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest leading-none">
            No containers awarded to you this time.
          </div>
          <p className="text-sm text-neutral-600 mt-4 max-w-xl">
            Thank you for quoting. Containers on this auction were allocated to other carriers. Details of those allocations are confidential.
          </p>
        </div>
      );
    }
    return (
      <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="freight-bidder-won">
        <div className="absolute top-0 right-0 bg-[#22D3EE] text-[#0B1929] font-semibold px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Allocated</div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
          <Boat size={14} weight="bold" /> You&apos;re carrying {myWins.length} container{myWins.length === 1 ? "" : "s"}
        </div>
        <div className="grid lg:grid-cols-2 gap-10 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Your awarded value</div>
            <div className="font-display font-extrabold tracking-tightest text-5xl lg:text-6xl leading-none klein-blue" data-testid="my-freight-won-total">
              {formatCurrency(a.my_shipment_wins_total || 0, a.currency)}
            </div>
          </div>
          <div className="text-sm text-neutral-600 leading-relaxed">
            Congratulations! The creator has allocated these containers to you at the vessel + ETD you committed. Please confirm bookings with the creator directly. Containers not listed below were awarded to other carriers and remain confidential.
          </div>
        </div>
        <FreightAllocationsTable rows={myWins} currency={a.currency} hideBidder testid="my-freight-wins-table" />
      </div>
    );
  }

  // ----- Creator (and super-admin) view -----
  return (
    <div className="border-2 border-black p-8 lg:p-12 bg-white relative" data-testid="freight-creator-awarded">
      <div className="absolute top-0 right-0 bg-[#0B1929] text-white px-3 py-1 text-[10px] tracking-[0.3em] uppercase">Closed</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3 flex items-center gap-2">
        <Boat size={14} weight="bold" /> Freight Allocated · {winnersByBidder.length} carrier{winnersByBidder.length === 1 ? "" : "s"}
      </div>
      <div className="grid lg:grid-cols-2 gap-10 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Total freight value</div>
          <div className="font-display font-extrabold tracking-tightest text-5xl lg:text-6xl leading-none klein-blue" data-testid="freight-total-value">
            {formatCurrency(totalAwardedValue, a.currency)}
          </div>
          <div className="text-xs text-neutral-600 mt-2">{allocations.length} container(s) allocated across {winnersByBidder.length} carrier(s)</div>
        </div>
        <div className="space-y-3" data-testid="freight-carrier-breakdown">
          <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">Carrier Breakdown</div>
          {winnersByBidder.map((v) => (
            <div key={v.email} className="border border-black/10 p-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-semibold">{v.name || v.email}</div>
                <div className="font-mono-plex font-semibold">{formatCurrency(v.total, a.currency)}</div>
              </div>
              <div className="text-[11px] font-mono-plex text-neutral-500">{v.email}{v.company ? ` · ${v.company}` : ""}</div>
              <div className="text-xs text-neutral-600 mt-1">{v.items.length} container{v.items.length === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      </div>
      <FreightAllocationsTable rows={allocations} currency={a.currency} testid="freight-allocations-table" />
    </div>
  );
};

const FreightAllocationsTable = ({ rows, currency, hideBidder, testid }) => (
  <div className="overflow-x-auto border-t border-black/10 pt-4" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-black/30 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
          <th className="text-left p-2 w-10">#</th>
          <th className="text-left p-2">Shipment</th>
          {!hideBidder && <th className="text-left p-2">Carrier</th>}
          <th className="text-left p-2 w-32">Container Pickup</th>
          <th className="text-left p-2">Vessel</th>
          <th className="text-left p-2 w-32">Committed ETD</th>
          <th className="text-right p-2 w-32">Rate</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.shipment_ref}-${i}`} className="border-b border-black/5">
            <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
            <td className="p-2 font-mono-plex font-semibold">{r.shipment_ref}</td>
            {!hideBidder && (
              <td className="p-2">
                <div className="font-semibold">{r.bidder_name || r.bidder_email}</div>
                <div className="text-[11px] font-mono-plex text-neutral-500">{r.bidder_email}</div>
              </td>
            )}
            <td className="p-2 font-mono-plex text-xs">{r.cargo_ready_date || "—"}</td>
            <td className="p-2">{r.vessel_name || "—"}</td>
            <td className="p-2 font-mono-plex text-xs">{r.etd_committed || "—"}</td>
            <td className="p-2 text-right font-mono-plex klein-blue font-semibold">{formatCurrency(r.rate, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
